import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  getPermissionAsync,
  isCurrentlySubscribed,
  isNativePushSupported,
  isPushSupported,
  isStandaloneDisplay,
} from "./pushClient";

const RECONNECT_DISMISS_KEY = "phieg.pwa.pushReconnectDismissedAt";
const INSTALL_DISMISS_KEY = "phieg.pwa.androidInstallDismissedAt";
const DISMISS_FOR_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function recentlyDismissed(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_FOR_MS;
  } catch {
    return false;
  }
}

function markDismissed(key) {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch {
    /* noop */
  }
}

/**
 * Top-of-layout banner with two jobs, in priority order:
 *   1. On Android (where the browser offers it), a one-tap "Install app" button
 *      that fires the native install prompt — so a logged-in client can install
 *      without ever visiting /get-app.
 *   2. Otherwise, for a user who HAD push on and lost the subscription, a
 *      "reconnect" nudge pointing at Settings.
 * Each banner has its own 7-day dismissal.
 *
 * The FIRST ask (permission never answered) is not here any more: that is
 * NotificationGate's job — a one-tap bottom sheet that subscribes on the spot,
 * since the old "set up push" banner only pointed at Settings and went
 * unanswered.
 */
export default function InstallPrompt() {
  const navigate = useNavigate();
  // A user who already granted browser permission before but has no live
  // subscription right now was previously opted in and got silently
  // disconnected (e.g. a dead subscription pruned server-side, or a stalled
  // auto-resubscribe after a service worker update) — worth an urgent nudge.
  const [pushVisible, setPushVisible] = useState(false);
  const [installEvent, setInstallEvent] = useState(
    typeof window !== "undefined" ? window.__deferredInstallPrompt || null : null
  );
  const [installDismissed, setInstallDismissed] = useState(
    recentlyDismissed(INSTALL_DISMISS_KEY)
  );

  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      window.__deferredInstallPrompt = e;
      setInstallEvent(e);
    };
    const onInstalled = () => {
      window.__deferredInstallPrompt = null;
      setInstallEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const canInstall = Boolean(installEvent) && !isStandaloneDisplay();

  const evaluatePush = useCallback(async () => {
    if (!isPushSupported()) return setPushVisible(false);
    // The store app reconnects by itself a moment after it opens
    // (usePushAutoResubscribe) and its only reminder is for a refusal
    // (NotificationGate). Read here, in the second before that, the phone
    // looked "disconnected" — e.g. after every sign-in, which drops the
    // saved token — and the nudge stayed up once it was connected.
    if (isNativePushSupported()) return setPushVisible(false);
    // Only someone who already granted permission can be "disconnected". The
    // never-asked case belongs to NotificationGate; "denied" is explained by
    // the Settings toggle.
    if ((await getPermissionAsync()) !== "granted") return setPushVisible(false);
    const alreadyOn = await isCurrentlySubscribed();
    if (alreadyOn) return setPushVisible(false);
    if (recentlyDismissed(RECONNECT_DISMISS_KEY)) return setPushVisible(false);
    setPushVisible(true);
  }, []);

  useEffect(() => {
    evaluatePush();
  }, [evaluatePush]);

  const handleInstall = async () => {
    const evt = installEvent || window.__deferredInstallPrompt;
    if (!evt) return;
    evt.prompt();
    try {
      await evt.userChoice;
    } catch (_) {
      /* dismissed */
    }
    window.__deferredInstallPrompt = null;
    setInstallEvent(null);
  };

  const dismissInstall = (e) => {
    e.stopPropagation();
    markDismissed(INSTALL_DISMISS_KEY);
    setInstallDismissed(true);
  };

  const goToSettings = () => navigate("/settings");
  const dismissPush = (e) => {
    e.stopPropagation();
    markDismissed(RECONNECT_DISMISS_KEY);
    setPushVisible(false);
  };

  // Priority 1 — one-tap install (Android).
  if (canInstall && !installDismissed) {
    return (
      <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 1.5 }}>
        <Alert
          severity="info"
          variant="outlined"
          onClose={dismissInstall}
          sx={{
            borderColor: (t) => t.palette.extras?.accent?.main || "#7fb09a",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 2 }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ width: "100%" }}
          >
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
              Install Phieg CRM on your phone for one-tap access and instant lead
              notifications.
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={handleInstall}
              sx={{
                alignSelf: { xs: "stretch", sm: "center" },
                bgcolor: (t) => t.palette.extras?.accent?.main || "#7fb09a",
                "&:hover": {
                  bgcolor: (t) => t.palette.extras?.accent?.dark || "#5f9078",
                },
              }}
            >
              Install app
            </Button>
          </Stack>
        </Alert>
      </Box>
    );
  }

  // Priority 2 — reconnect nudge.
  if (!pushVisible) return null;

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 1.5 }}>
      <Alert
        severity="warning"
        variant="outlined"
        onClose={dismissPush}
        onClick={goToSettings}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            goToSettings();
          }
        }}
        sx={{
          cursor: "pointer",
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 2 }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ width: "100%" }}
        >
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
            Your mobile notifications got disconnected — reconnect them so you
            don't miss a new lead.
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              goToSettings();
            }}
            sx={{
              alignSelf: { xs: "stretch", sm: "center" },
              bgcolor: (t) => t.palette.extras?.accent?.main || "#7fb09a",
              "&:hover": {
                bgcolor: (t) => t.palette.extras?.accent?.dark || "#5f9078",
              },
            }}
          >
            Reconnect
          </Button>
        </Stack>
      </Alert>
    </Box>
  );
}
